import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';

export default function JobDetail() {
  const { projectId } = useParams();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileRef = useRef();

  useEffect(() => { fetchJob(); }, [projectId]);

  async function fetchJob() {
    try {
      const { data } = await api.get(`/jobs/${projectId}`);
      setJob(data);
      setUploads(data.uploads || []);
    } finally {
      setLoading(false);
    }
  }

  async function handleUpload(e) {
    e.preventDefault();
    const file = fileRef.current?.files[0];
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['pdf', 'dwg'].includes(ext)) {
      setUploadError('Only PDF and DWG files are accepted');
      return;
    }
    setUploadError('');
    setUploading(true);
    const form = new FormData();
    form.append('file', file);
    try {
      await api.post(`/files/upload/${projectId}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      fileRef.current.value = '';
      await fetchJob();
    } catch (err) {
      setUploadError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  if (loading) return <Layout><p className="text-gray-500 text-sm">Loading…</p></Layout>;
  if (!job) return <Layout><p className="text-red-400 text-sm">Job not found</p></Layout>;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <p className="text-xs font-mono text-gray-500 mb-1">{job.project_id}</p>
          <h1 className="text-xl font-bold text-white">{job.project_name || 'Unnamed Project'}</h1>
          <p className="text-sm text-gray-400">{job.client_name} &middot; {job.site_address}</p>
        </div>

        {/* Upload Section — CAD only */}
        {(user.role === 'cad' || user.role === 'admin') && (
          <div className="rounded-xl border border-navy-500 p-5 mb-6" style={{ backgroundColor: '#142236' }}>
            <h2 className="font-semibold text-gray-300 mb-3 text-sm uppercase tracking-wide">Upload Document to Salesforce</h2>
            <form onSubmit={handleUpload} className="flex items-center gap-3">
              <input
                type="file"
                accept=".pdf,.dwg"
                ref={fileRef}
                className="text-sm text-gray-400 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:font-bold file:uppercase file:tracking-wide file:bg-navy-700 file:text-gold-500 hover:file:bg-navy-600 file:transition"
              />
              <button
                type="submit"
                disabled={uploading}
                className="bg-gold-500 text-navy-900 text-sm px-4 py-2 rounded font-bold uppercase tracking-wide hover:bg-gold-400 disabled:opacity-50 transition whitespace-nowrap"
              >
                {uploading ? 'Uploading…' : 'Upload & Push to SF'}
              </button>
            </form>
            {uploadError && <p className="text-red-400 text-xs mt-2">{uploadError}</p>}
          </div>
        )}

        {/* Uploaded Documents */}
        <div className="rounded-xl border border-navy-500 overflow-hidden" style={{ backgroundColor: '#142236' }}>
          <div className="px-5 py-3 border-b border-navy-500">
            <h2 className="font-semibold text-gray-300 text-sm uppercase tracking-wide">Documents ({uploads.length})</h2>
          </div>
          {uploads.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-8">No documents uploaded yet</p>
          ) : (
            <table className="w-full text-sm">
              <thead style={{ backgroundColor: '#0f1a29' }}>
                <tr>
                  {['File', 'Type', 'Uploaded By', 'Date', 'Salesforce'].map((h) => (
                    <th key={h} className="text-left px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-navy-600">
                {uploads.map((u) => (
                  <tr key={u.id} className="hover:bg-navy-600 transition">
                    <td className="px-4 py-3 font-medium text-white">{u.file_name}</td>
                    <td className="px-4 py-3">
                      <span className="uppercase text-xs font-bold px-2 py-0.5 rounded bg-navy-700 text-gold-500">
                        {u.file_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-400">{u.uploaded_by_name || '—'}</td>
                    <td className="px-4 py-3 text-gray-400">
                      {new Date(u.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      {u.pushed_to_sf ? (
                        <span className="text-green-400 text-xs font-medium">Pushed</span>
                      ) : (
                        <span className="text-gray-500 text-xs">Pending</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Layout>
  );
}
